import { useRef, useEffect, useCallback } from 'react';

type UseInfiniteScrollOptions = {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  threshold?: number;
};

/**
 * Hook for infinite scroll functionality using IntersectionObserver.
 * Returns a ref to attach to a sentinel element at the bottom of your list.
 *
 * @example
 * const { observerTarget } = useInfiniteScroll({
 *   hasMore,
 *   isLoading,
 *   onLoadMore: loadMoreItems,
 * });
 *
 * return (
 *   <div>
 *     {items.map(item => <Item key={item.id} />)}
 *     {hasMore && <div ref={observerTarget} />}
 *   </div>
 * );
 */
export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  threshold = 0.1,
}: UseInfiniteScrollOptions) => {
  const observerTarget = useRef<HTMLDivElement>(null);

  const handleLoadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      onLoadMore();
    }
  }, [isLoading, hasMore, onLoadMore]);

  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          handleLoadMore();
        }
      },
      { threshold },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoading, handleLoadMore, threshold]);

  return { observerTarget };
};
